/**
 * 同意書管理 一覧画面 (Phase 2)。
 *
 * 資料管理画面 (DocumentsListPage) と同じカードグリッド UI。
 * 用途別テンプレート選択ではなく、シンプルな「新規作成」フロー。
 */
import type { ConsentDocumentStatus } from '@goenhub/shared';
import { Loader2, Plus, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useConsentDocuments,
  useUpsertConsentDocument,
} from '@/hooks/useConsentDocuments';
import { useProductCategoryNameMap } from '@/hooks/useProductCategories';
import { paths } from '@/routes/paths';

const STATUS_LABEL: Record<ConsentDocumentStatus, string> = {
  draft: '下書き',
  published: '公開中',
  archived: 'アーカイブ',
};

const STATUS_BADGE_VARIANT: Record<
  ConsentDocumentStatus,
  'accent' | 'draft' | 'default'
> = {
  draft: 'draft',
  published: 'accent',
  archived: 'default',
};

function formatDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      return (d as { toDate(): Date }).toDate().toLocaleDateString('ja-JP');
    } catch {
      return '';
    }
  }
  return '';
}

export function ConsentDocumentsListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useConsentDocuments();
  const upsert = useUpsertConsentDocument();
  const categoryNameMap = useProductCategoryNameMap();
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await upsert.mutateAsync({
        title: '無題の同意書',
        status: 'draft',
        sections: [], // signature は backend で末尾に自動追加
        primaryCategoryId: null,
        logoStoragePath: null,
        logoPosition: null,
        stampStoragePath: null,
        stampPosition: null,
        stampHidden: false,
      });
      navigate(paths.consentDocumentEdit.replace(':id', r.id));
    } catch (e) {
      toast.error(`作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Screen
      title="同意書管理"
      description="商談で送付する同意書のテンプレ管理。成約後に「同意書送付」ボタンから公開リンクを発行し、サイン提出を受け付ける。"
      actions={
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleCreate()}
          disabled={creating}
        >
          {creating ? (
            <Loader2 className="size-4 mr-1 animate-spin" />
          ) : (
            <Plus className="size-4 mr-1" />
          )}
          新規作成
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <ShieldCheck className="size-12 text-[var(--text-muted)]" />
          <p className="text-[var(--text-muted)]">まだ同意書がありません。</p>
          <Button onClick={() => void handleCreate()} disabled={creating}>
            <Plus className="size-4 mr-1" /> 最初の同意書を作成
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data?.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() =>
                navigate(paths.consentDocumentEdit.replace(':id', d.id))
              }
              className="text-left p-4 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold text-sm truncate">{d.title}</h3>
                <Badge variant={STATUS_BADGE_VARIANT[d.status]}>
                  {STATUS_LABEL[d.status]}
                </Badge>
              </div>
              <p className="text-xs text-[var(--text-muted)] mb-2 truncate">
                {d.primaryCategoryId
                  ? `主軸: ${categoryNameMap.get(d.primaryCategoryId) ?? '(削除済)'}`
                  : '主軸カテゴリー未設定'}
              </p>
              <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                <span>{d.sections?.length ?? 0} セクション</span>
                <span>更新 {formatDate(d.updatedAt)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Screen>
  );
}
